import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from '@apollo/client/react';
import { Badge, Box, BoxProps, Button, chakra, HStack, IconButton, Spinner, Text } from '@chakra-ui/react';
import { HoverCardContent, HoverCardRoot, HoverCardTrigger } from '@src/components/chakra-ui/hover-card';
import { Tooltip } from '@src/components/chakra-ui/tooltip';
import { ChatGenerateControl } from '@src/components/sco/Chat/ChatGenerateControl';
import { ChatTypingIndicator } from '@src/components/sco/Chat/ChatThreadTypingIndicator';
import { ChatConnectionStatus } from '@src/components/sco/Chat/Layout/ChatConnectionStatus';
import { draftKey, liveFiles, sameDraftKey, serverDraftAction, serverDraftKey } from '@src/components/sco/Chat/chatCrmDraft';
import { useCrmChatDraft } from '@src/components/sco/Chat/useCrmChatDraft';
import { type CommentAttachmentFile, CommentPendingAttachments, COMMENT_STORED_FILES_SELECTION, mapInitialFiles, useCommentAttachments } from '@src/components/sco/Comments/CommentAttachments';
import { OnboardingHighlightTarget, ONBOARDING_HIGHLIGHT_IDS } from '@src/components/sco/Requests/RequestOnboarding/OnboardingHighlightTarget';
import { RichMarkdownInput } from '@src/components/sco/RichMarkdownInput';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { gql } from '@src/lib/gql';
import { useDebounce } from '@uidotdev/usehooks';
import React, { useEffect, useRef, useState } from 'react';
import { LuArrowUp, LuPlus, LuSave, LuSend, LuTrash2 } from 'react-icons/lu';
type ChatDraft = {
  commentId?: string | null;
  body?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  storedFiles?: {
    items?: Array<CommentAttachmentFile | null> | null;
  } | null;
};
type Props = {
  boxProps?: BoxProps;
  newCommentValues: {
    attachmentId?: string;
    attachmentType?: string;
    parentCommentId?: string;
    parentCommentNodeId?: string;
    threadId?: string;
  };
  onSuccess?: (mutationResponse) => Promise<any> | any;
  onError?: (error) => Promise<any> | any;
  onCancel?: () => Promise<any> | any;
  placeholder?: string;
  enableFileAttachments?: boolean;
};
export const ChatMessageComposer = (props: Props) => {
  const {
    currentUser
  } = useCurrentUser();
  const [body, setBody] = useState('');
  const [draftCommentId, setDraftCommentId] = useState<string | null>(null);
  const [editorSeed, setEditorSeed] = useState('new');
  const [initialMarkdown, setInitialMarkdown] = useState('');
  const debouncedBody = useDebounce(body, 3000);
  const [isTyping, setIsTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitTrigger, setSubmitTrigger] = useState(0);
  const [submissionCount, setSubmissionCount] = useState(0);
  const [generateBusy, setGenerateBusy] = useState(false);
  const {
    pendingFiles,
    addFile,
    removeFile,
    reset,
    isUploading,
    isFull,
    storedFileIds,
    setPendingFiles
  } = useCommentAttachments();
  const [upsertChatComment, upsertChatCommentMutation] = useMutation(ChatMessageComposer.mutations.upsertChatComment());
  const [deleteComment] = useMutation(ChatMessageComposer.mutations.deleteComment());
  // Set synchronously so a second Enter or click lands before isLoading re-renders.
  // The box keeps its text until the send returns, so without this each press posts it again.
  const inFlightRef = useRef(false);
  const threadId = props.newCommentValues.threadId;
  const parentCommentId = props.newCommentValues.parentCommentId;
  const threadIdRef = useRef(threadId);
  useEffect(() => {
    threadIdRef.current = threadId;
  }, [threadId]);
  // CRM mode (crm-chat-draft-sync L2, L8): on a crm account's own CRM conversation, the conversation's
  // CRM reply draft is a pinned entry in the drafts menu. Saving stays manual, as for any draft: opening
  // it puts it in the box, Save updates it (the CRM inbox has the same draft), Send sends it through the
  // desk's send path, Delete discards it. While it is open the box follows edits made on the desk, asks
  // before replacing its own edits, and locks while someone else holds the reply (L3). Files go with it.
  const crm = useCrmChatDraft({
    enabled: !!currentUser,
    parentCommentId,
    threadId,
    userId: currentUser?.userId
  });
  const crmRef = useRef(crm);
  useEffect(() => {
    crmRef.current = crm;
  });
  const crmOn = crm.on;
  // Drafts for admins, as before, and for a crm account on its own CRM conversation.
  const canUseDrafts = !!currentUser?.isAdmin || crmOn;
  const crmDraft = crmOn ? crm.context?.draft ?? null : null;
  const [crmOpen, setCrmOpen] = useState(false);
  const crmLockedBy = crmOpen ? crm.lockedBy : null;
  const bodyRef = useRef('');
  const fileIdsRef = useRef<string[]>([]);
  useEffect(() => {
    fileIdsRef.current = storedFileIds;
  }, [storedFileIds]);
  // What the CRM draft read when this box opened it or last saved it.
  const crmSavedKey = useRef<string | null>(null);
  const [crmConflict, setCrmConflict] = useState(false);
  const [crmNote, setCrmNote] = useState<string | null>(null);
  const [crmError, setCrmError] = useState<string | null>(null);
  const crmServerKey = crmOn ? serverDraftKey(crm.context) : null;

  /** The box text, kept in a ref too for the async handlers. */
  const setBoxBody = (text: string) => {
    bodyRef.current = text;
    setBody(text);
  };

  /** Put the server's CRM draft in the box, unless the box already reads the same (`force` reloads it). */
  const placeCrmDraft = (force = false) => {
    const key = serverDraftKey(crmRef.current.context);
    crmSavedKey.current = key;
    setCrmConflict(false);
    if (!force && sameDraftKey(draftKey(bodyRef.current, fileIdsRef.current), key)) {
      return;
    }
    const draft = crmRef.current.context?.draft;
    const text_0 = draft?.body || '';
    setBoxBody(text_0);
    setInitialMarkdown(text_0);
    setEditorSeed(`crm-${Date.now()}`);
    setPendingFiles(mapInitialFiles(liveFiles(draft?.storedFiles)));
  };

  /** Open the CRM draft from the drafts menu. */
  const openCrmDraft = () => {
    setDraftCommentId(null);
    setCrmError(null);
    setCrmNote(null);
    setCrmOpen(true);
    placeCrmDraft(true);
  };
  const closeCrmDraft = () => {
    setCrmOpen(false);
    crmSavedKey.current = null;
    setCrmConflict(false);
  };

  // Generate is sco-admin only: the mutation refuses every other admin.
  const canOfferGenerate = !!currentUser?.isScoAdmin && !parentCommentId && !!threadId;
  const draftsQuery = useQuery(ChatMessageComposer.queries.myDrafts(), {
    skip: !threadId || !canUseDrafts,
    variables: {
      collectionId: `ChatThread:${threadId}:${parentCommentId || 0}:drafts`,
      order: [['updatedAt', 'DESC']],
      pageSize: 50,
      parentCommentId: parentCommentId || null,
      threadId
    }
  });
  const drafts = draftsQuery.data?.forum?.comments?.items || [];
  const [upsertAttachmentUserPresence] = useMutation(gql`
    mutation ($input: UpsertAttachmentUserPresenceInput!) {
      upsertAttachmentUserPresence(input: $input) {
        success
      }
    }
  `);
  const attachmentType = props.newCommentValues.parentCommentId ? 'Comment' : 'Thread';
  const attachmentId = props.newCommentValues.parentCommentId || props.newCommentValues.threadId;

  // Submit Handler
  const commentInput = (isDraft: boolean) => {
    const allowFiles = props.enableFileAttachments !== false;
    const fileIds = allowFiles ? storedFileIds : [];
    return {
      body: body || '',
      ...props.newCommentValues,
      ...(draftCommentId ? {
        commentId: draftCommentId
      } : {}),
      isDraft,
      // An open draft always sends its list, so a file taken off it is taken off the draft too.
      ...(fileIds.length > 0 || draftCommentId ? {
        storedFileIds: fileIds
      } : {})
    };
  };
  const canCompose = () => {
    const allowFiles_0 = props.enableFileAttachments !== false;
    const fileIds_0 = allowFiles_0 ? storedFileIds : [];
    return stripMarkdownWhitespace(body).length > 0 || fileIds_0.length > 0;
  };

  /** Post as the CRM reply through the desk's send path: the open CRM draft, or the box as a new reply. */
  const sendCrmReply = async () => {
    if (crmLockedBy || isUploading || inFlightRef.current) {
      return;
    }
    // The desk's send needs words; files go with them.
    if (stripMarkdownWhitespace(bodyRef.current).length === 0) {
      setCrmError('Write a message to send with your files.');
      return;
    }
    inFlightRef.current = true;
    setIsLoading(true);
    try {
      const commentId = crmOpen ? crmRef.current.context?.draft?.commentId || null : null;
      const result = await crmRef.current.send(bodyRef.current, fileIdsRef.current, commentId);
      if (!result.ok) {
        setCrmError(result.error);
        return;
      }
      setCrmError(null);
      setCrmNote(null);
      closeCrmDraft();
      setBoxBody('');
      setInitialMarkdown('');
      setEditorSeed(`sent-${Date.now()}`);
      reset();
      setSubmissionCount(count => count + 1);
      void crmRef.current.refetch();
      if (props.onSuccess) {
        await props.onSuccess(result);
      }
    } finally {
      inFlightRef.current = false;
      setIsLoading(false);
    }
  };

  /** Save the box as the CRM draft: the open one, or a new one when the conversation has none. */
  const saveCrmDraft = async () => {
    if (crmLockedBy || isUploading || inFlightRef.current) {
      return;
    }
    // An empty text would discard the CRM draft; Delete in the menu does that.
    if (stripMarkdownWhitespace(bodyRef.current).length === 0) {
      setCrmError('Write the reply before saving it.');
      return;
    }
    inFlightRef.current = true;
    setIsLoading(true);
    try {
      crmRef.current.claim();
      const result = await crmRef.current.save(bodyRef.current, fileIdsRef.current);
      if (!result.ok) {
        setCrmError(result.error);
        return;
      }
      crmSavedKey.current = draftKey(bodyRef.current, fileIdsRef.current);
      setDraftCommentId(null);
      setCrmOpen(true);
      setCrmConflict(false);
      setCrmError(null);
      setCrmNote('Saved. Your CRM inbox has the same draft.');
      void crmRef.current.refetch();
    } finally {
      inFlightRef.current = false;
      setIsLoading(false);
    }
  };

  /** Send the CRM draft from the menu, as it is saved. */
  const sendCrmDraft = async () => {
    const draft_0 = crmRef.current.context?.draft;
    if (!draft_0?.commentId || crm.lockedBy || inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    setIsLoading(true);
    try {
      const result = await crmRef.current.send(draft_0.body || '', null, draft_0.commentId);
      if (!result.ok) {
        setCrmError(result.error);
        return;
      }
      setCrmError(null);
      if (crmOpen) {
        clearComposer(`sent-${Date.now()}`);
        setSubmissionCount(count => count + 1);
      }
      void crmRef.current.refetch();
      if (props.onSuccess) {
        await props.onSuccess(result);
      }
    } finally {
      inFlightRef.current = false;
      setIsLoading(false);
    }
  };

  /** Delete the CRM draft from the menu (the desk's discard). */
  const deleteCrmDraft = async () => {
    if (crm.lockedBy || inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    setIsLoading(true);
    try {
      const result = await crmRef.current.discard();
      if (!result.ok) {
        setCrmError(result.error);
        return;
      }
      setCrmError(null);
      if (crmOpen) {
        clearComposer(`cleared-${Date.now()}`);
      }
      void crmRef.current.refetch();
    } finally {
      inFlightRef.current = false;
      setIsLoading(false);
    }
  };
  const handleSubmit = async () => {
    // A personal draft that is open is published as before; anything else is the CRM reply.
    if (crmOn && !draftCommentId) {
      await sendCrmReply();
      return;
    }
    if (!canCompose() || isUploading || inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    let mutationResponse = null;
    setIsLoading(true);
    try {
      mutationResponse = await upsertChatComment({
        variables: {
          input: commentInput(false)
        }
      });
      setBoxBody('');
      setDraftCommentId(null);
      setInitialMarkdown('');
      setEditorSeed(`sent-${Date.now()}`);
      reset();
      await draftsQuery.refetch();
      setSubmissionCount(count => count + 1);
      if (props.onSuccess) {
        await props.onSuccess(mutationResponse);
      }
    } catch (err) {
      if (props.onError) {
        await props.onError(err);
      } else {
        alert('Comment could not be posted.');
      }
    } finally {
      inFlightRef.current = false;
    }
    setIsLoading(false);
  };
  const clearComposer = (seed: string) => {
    setDraftCommentId(null);
    setCrmOpen(false);
    crmSavedKey.current = null;
    setCrmConflict(false);
    setBoxBody('');
    setInitialMarkdown('');
    setEditorSeed(seed);
    reset();
  };
  const saveDraft = async () => {
    // The CRM draft is the conversation's reply: Save updates it when open, and makes it when there is
    // none yet. With a CRM draft already there, a fresh save is a personal draft as before.
    if (crmOn && (crmOpen || !draftCommentId && !crmRef.current.context?.draft)) {
      await saveCrmDraft();
      return;
    }
    if (!canCompose() || isUploading || inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    const updatingOpenDraft = !!draftCommentId;
    setIsLoading(true);
    try {
      await upsertChatComment({
        variables: {
          input: commentInput(true)
        }
      });
      await draftsQuery.refetch();
      // A fresh save is a new draft. Clear the box so the next save is another one.
      // Saving while a draft is open updates that draft and leaves it open.
      if (!updatingOpenDraft) {
        clearComposer(`saved-${Date.now()}`);
      }
    } catch (err_0) {
      if (props.onError) {
        await props.onError(err_0);
      } else {
        alert('Draft could not be saved.');
      }
    } finally {
      inFlightRef.current = false;
    }
    setIsLoading(false);
  };
  const sendDraft = async (draft_1: ChatDraft) => {
    if (!draft_1.commentId || isUploading || inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    setIsLoading(true);
    try {
      const mutationResponse_0 = await upsertChatComment({
        variables: {
          input: {
            body: draft_1.body || '',
            ...props.newCommentValues,
            commentId: draft_1.commentId,
            isDraft: false
          }
        }
      });
      if (draftCommentId === draft_1.commentId) {
        clearComposer(`sent-${Date.now()}`);
        setSubmissionCount(count_0 => count_0 + 1);
      }
      await draftsQuery.refetch();
      if (props.onSuccess) {
        await props.onSuccess(mutationResponse_0);
      }
    } catch (err_1) {
      if (props.onError) {
        await props.onError(err_1);
      } else {
        alert('Draft could not be posted.');
      }
    } finally {
      inFlightRef.current = false;
    }
    setIsLoading(false);
  };
  const loadDraft = (draft_2: ChatDraft) => {
    if (!draft_2.commentId) {
      return;
    }
    const nextBody = draft_2.body || '';
    closeCrmDraft();
    setCrmNote(null);
    setDraftCommentId(draft_2.commentId);
    setBoxBody(nextBody);
    setInitialMarkdown(nextBody);
    setEditorSeed(`${draft_2.commentId}-${Date.now()}`);
    // The draft's files come back with it, so saving it again keeps them.
    setPendingFiles(mapInitialFiles(draft_2.storedFiles?.items));
  };
  const removeDraft = async (commentId: string) => {
    setIsLoading(true);
    try {
      await deleteComment({
        variables: {
          input: {
            commentId
          }
        }
      });
      if (draftCommentId === commentId) {
        setDraftCommentId(null);
        setBoxBody('');
        setInitialMarkdown('');
        setEditorSeed(`cleared-${Date.now()}`);
      }
      await draftsQuery.refetch();
    } catch (err_2) {
      if (props.onError) {
        await props.onError(err_2);
      } else {
        alert('Draft could not be deleted.');
      }
    }
    setIsLoading(false);
  };

  // A new conversation starts fresh: nothing open, nothing carried over.
  useEffect(() => {
    setCrmOpen(false);
    crmSavedKey.current = null;
    setCrmConflict(false);
    setCrmError(null);
    setCrmNote(null);
  }, [threadId]);

  // While the CRM draft is open, follow it on every re-read (D6).
  useEffect(() => {
    if (!crmOn || !crmOpen || crmServerKey == null || crmSavedKey.current == null) {
      return;
    }
    if (!crmRef.current.context?.draft) {
      // Sent or cleared in the CRM: the text stays in the box, as a new reply.
      closeCrmDraft();
      setCrmNote('This draft was sent or cleared in the CRM. Your text is still here.');
      return;
    }
    if (crmLockedBy) {
      // Someone else holds the reply: show what they have.
      placeCrmDraft();
      return;
    }
    const action = serverDraftAction({
      boxKey: draftKey(bodyRef.current, fileIdsRef.current),
      savedKey: crmSavedKey.current,
      serverKey: crmServerKey
    });
    if (action === 'adopt') {
      placeCrmDraft();
    } else if (action === 'conflict') {
      setCrmConflict(true);
    }
  }, [crmOn, crmOpen, crmServerKey, crmLockedBy]);

  // Handle Submit Trigger
  useEffect(() => {
    if (submitTrigger > 0) {
      handleSubmit();
    }
  }, [submitTrigger]);

  // Typing Handler
  useEffect(() => {
    setIsTyping(stripMarkdownWhitespace(body).length > 0 && body != debouncedBody);
  }, [body, debouncedBody]);
  useEffect(() => {
    if (attachmentType && attachmentId) {
      upsertAttachmentUserPresence({
        variables: {
          input: {
            attachmentType,
            attachmentId,
            isTyping,
            isOnline: true,
            userId: currentUser?.userId
          }
        }
      });
    }
  }, [isTyping]);
  if (!currentUser) {
    return <Spinner />;
  }
  return <Box
  // Size container so ChatConnectionStatus density="auto" can hide the label when tight.
  // Tighter top so messages sit closer to the field now that connection status is in-toolbar.
  css={{
    containerType: 'inline-size'
  }} paddingBottom={2} paddingTop={1} paddingX={2} width={'full'} {...props.boxProps}>
      {/* Only takes space while someone is typing — no reserved gap above the field */}
      <ChatTypingIndicator attachmentId={attachmentId} attachmentType={attachmentType} />
      <OnboardingHighlightTarget borderRadius={'lg'} id={ONBOARDING_HIGHLIGHT_IDS.aiChat}>
        <Box width={'full'}>
          <RichMarkdownInput key={`thread-${props.newCommentValues.threadId}-${editorSeed}-${submissionCount}`} autoFocus={submissionCount > 0} disabled={generateBusy || Boolean(crmLockedBy)} enableFileAttachments={props.enableFileAttachments !== false} initialMarkdownValue={initialMarkdown} placeholder={props.placeholder || 'Type your reply...'} additionalToolbarButtons={<HStack align={'center'} flexShrink={0} gap={2}>
                <ChatConnectionStatus density={'auto'} />
                {canOfferGenerate && <ChatGenerateControl body={body} busy={isUploading || isLoading} threadId={threadId} userId={currentUser.userId} onPending={setGenerateBusy} onWrite={(markdown, writtenForThreadId) => {
            if (writtenForThreadId !== threadIdRef.current) {
              return;
            }
            setBoxBody(markdown);
            setInitialMarkdown(markdown);
            setEditorSeed(`generated-${Date.now()}`);
          }} />}
                {canUseDrafts && <DraftSaveButton busy={isUploading || isLoading || generateBusy || Boolean(crmLockedBy)} canSave={canCompose()} crmDraft={crmDraft} crmLocked={Boolean(crm.lockedBy)} crmOpen={crmOpen} drafts={drafts} openDraftId={draftCommentId} onDelete={removeDraft} onDeleteCrm={() => void deleteCrmDraft()} onNew={() => clearComposer(`new-${Date.now()}`)} onOpen={loadDraft} onOpenCrm={openCrmDraft} onSave={() => saveDraft()} onSend={sendDraft} onSendCrm={() => void sendCrmDraft()} />}
                <IconButton aria-label={'Post'} borderRadius={'md'} disabled={isUploading || generateBusy || Boolean(crmLockedBy)} loading={isLoading || isUploading || generateBusy || upsertChatCommentMutation.loading} size={'sm'} variant={'cta'} onClick={() => handleSubmit()}>
                  <LuArrowUp size={18} />
                </IconButton>
              </HStack>} fileAttachmentsDisabled={isFull || isUploading || isLoading || generateBusy || Boolean(crmLockedBy)} pluginConfigs={{
          hotKeys: {
            hotKeys: {
              enter: {
                func: event => {
                  // A held Enter auto-repeats; Enter while composing (IME) picks a candidate.
                  const native = (event as any)?.nativeEvent ?? event;
                  if (event?.repeat || native?.isComposing) {
                    return;
                  }
                  setSubmitTrigger(x => x + 1);
                }
              }
            }
          }
        }} onChange={values => {
          const next = values.markdown ?? '';
          if (crmOpen && next !== bodyRef.current) {
            crm.claim();
          }
          setBoxBody(next);
        }} onFileAttachmentSelected={file => {
          if (crmOpen) {
            crm.claim();
          }
          return addFile(file);
        }} />
          <CommentPendingAttachments disabled={isLoading || Boolean(crmLockedBy)} files={pendingFiles} onRemove={removeFile} />
          {crmOn ? <ChatCrmDraftLine conflict={crmConflict} error={crmError} lockedBy={crmLockedBy} note={crmNote} open={crmOpen} onLoad={() => placeCrmDraft()} onKeepMine={() => {
          // Keep this box; the next Save writes it over the CRM's version.
          crmSavedKey.current = serverDraftKey(crmRef.current.context);
          setCrmConflict(false);
        }} /> : null}
        </Box>
      </OnboardingHighlightTarget>
    </Box>;
};
ChatMessageComposer.mutations = {
  upsertChatComment: () => gql`
    mutation ($input: UpsertChatCommentInput!) {
      upsertChatComment(input: $input) {
        comment {
          id
          commentId
          isDraft
          ${COMMENT_STORED_FILES_SELECTION}
        }
      }
    }
  `,
  deleteComment: () => gql`
    mutation ($input: DeleteCommentInput!) {
      deleteComment(input: $input) {
        comment {
          id
          commentId
          deletedAt
        }
      }
    }
  `
};
ChatMessageComposer.queries = {
  myDrafts: () => gql`
    query (
      $collectionId: String
      $order: [[String]]
      $pageSize: Int
      $parentCommentId: String
      $threadId: String
    ) {
      forum {
        id
        comments(
          collectionId: $collectionId
          onlyMyDrafts: true
          order: $order
          pageSize: $pageSize
          parentCommentId: $parentCommentId
          threadId: $threadId
        ) {
          id
          items {
            id
            commentId
            body
            isDraft
            createdAt
            updatedAt
            ${COMMENT_STORED_FILES_SELECTION}
          }
        }
      }
    }
  `
};
export const stripMarkdownWhitespace = (str: string) => {
  return (str || '').replaceAll('\n', '').replaceAll('\r', '').replaceAll('<br>', '').trim();
};

/**
 * Under the box in CRM mode: who else is working the reply (the open CRM draft is locked), a change made
 * in the CRM while this box had its own edits (Load / Keep mine), an error, or what the box is now.
 */
function ChatCrmDraftLine(props) {
  const $ = _c(14);
  if (props.lockedBy) {
    let t0;
    if ($[0] !== props.lockedBy) {
      t0 = <Text color="fg.muted" fontSize="xs" paddingTop={1} role="status">{props.lockedBy} is working on this reply in the CRM. You can edit it again when they finish.</Text>;
      $[0] = props.lockedBy;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  if (props.conflict) {
    let t0;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <Text color="fg" fontSize="xs">This reply was changed in the CRM.</Text>;
      $[2] = t0;
    } else {
      t0 = $[2];
    }
    let t1;
    if ($[3] !== props.onLoad) {
      t1 = <Button size="2xs" variant="outline" onClick={props.onLoad}>Load it</Button>;
      $[3] = props.onLoad;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    let t2;
    if ($[5] !== props.onKeepMine) {
      t2 = <Button size="2xs" variant="ghost" onClick={props.onKeepMine}>Keep mine</Button>;
      $[5] = props.onKeepMine;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    let t3;
    if ($[7] !== t1 || $[8] !== t2) {
      t3 = <HStack gap={2} paddingTop={1} role="alert" wrap="wrap">{t0}{t1}{t2}</HStack>;
      $[7] = t1;
      $[8] = t2;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    return t3;
  }
  if (props.error) {
    let t0;
    if ($[10] !== props.error) {
      t0 = <Text color="fg.error" fontSize="xs" paddingTop={1} role="alert">{props.error}</Text>;
      $[10] = props.error;
      $[11] = t0;
    } else {
      t0 = $[11];
    }
    return t0;
  }
  const line = props.note || (props.open ? "Editing your CRM reply draft. Save updates it in your CRM inbox too." : null);
  let t0;
  if ($[12] !== line) {
    t0 = line ? <Text color="fg.subtle" fontSize="xs" paddingTop={1} role="status">{line}</Text> : null;
    $[12] = line;
    $[13] = t0;
  } else {
    t0 = $[13];
  }
  return t0;
}
function DraftSaveButton(props) {
  const $ = _c(43);
  const count = props.drafts.length + (props.crmDraft ? 1 : 0);
  const saveDisabled = props.busy || count === 0 && !props.canSave;
  const t0 = count > 0 ? `Save draft, ${count} saved` : "Save draft";
  let t1;
  if ($[0] !== saveDisabled) {
    t1 = saveDisabled ? {
      pointerEvents: "none"
    } : undefined;
    $[0] = saveDisabled;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== props) {
    t2 = () => {
      if (props.canSave) {
        props.onSave();
      }
    };
    $[2] = props;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <LuSave size={18} />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== saveDisabled || $[6] !== t0 || $[7] !== t1 || $[8] !== t2) {
    t4 = <IconButton aria-label={t0} borderRadius="md" css={t1} disabled={saveDisabled} size="sm" variant="ghost" onClick={t2}>{t3}</IconButton>;
    $[5] = saveDisabled;
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== count) {
    t5 = count > 0 && <Box alignItems="center" bg="fg" borderRadius="full" boxShadow="0 0 0 2px var(--chakra-colors-bg)" color="bg" display="flex" fontSize="10px" fontWeight="semibold" height="16px" justifyContent="center" lineHeight="1" minWidth="16px" paddingX="3px" pointerEvents="none" position="absolute" right="-3px" top="-3px">{count}</Box>;
    $[10] = count;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <Box display="inline-flex" position="relative">{t4}{t5}</Box>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const button = t6;
  if (count === 0) {
    let t7;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = {
        placement: "top"
      };
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    let t8;
    if ($[16] !== button) {
      t8 = <Tooltip borderRadius="6px" content="Save draft" fontSize="11px" fontWeight="bold" letterSpacing="0.05em" positioning={t7} textTransform="uppercase">{button}</Tooltip>;
      $[16] = button;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    return t8;
  }
  let t7;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      placement: "top-end"
    };
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== button) {
    t8 = <HoverCardTrigger asChild={true}>{button}</HoverCardTrigger>;
    $[19] = button;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Badge color="colorPalette.fg/50" paddingX={2} paddingY={1} size="xs" variant="title">Drafts</Badge>;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== props.crmOpen || $[23] !== props.onNew || $[24] !== props.openDraftId) {
    t10 = (props.openDraftId || props.crmOpen) && <HStack _hover={{
      bg: "colorPalette.subtle"
    }} borderRadius="md" cursor="pointer" gap={2} paddingX={2} paddingY={1} onClick={props.onNew}><LuPlus size={14} /><Text fontSize="sm">New draft</Text></HStack>;
    $[22] = props.crmOpen;
    $[23] = props.onNew;
    $[24] = props.openDraftId;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== props.busy || $[27] !== props.crmDraft || $[28] !== props.crmLocked || $[29] !== props.crmOpen || $[30] !== props.onDeleteCrm || $[31] !== props.onOpenCrm || $[32] !== props.onSendCrm) {
    t11 = props.crmDraft ? <CrmDraftRow busy={props.busy} draft={props.crmDraft} locked={Boolean(props.crmLocked)} open={Boolean(props.crmOpen)} onDelete={props.onDeleteCrm} onOpen={props.onOpenCrm} onSend={props.onSendCrm} /> : null;
    $[26] = props.busy;
    $[27] = props.crmDraft;
    $[28] = props.crmLocked;
    $[29] = props.crmOpen;
    $[30] = props.onDeleteCrm;
    $[31] = props.onOpenCrm;
    $[32] = props.onSendCrm;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== props) {
    t12 = props.drafts.map(draft => {
      if (!draft.commentId) {
        return null;
      }
      const preview = stripMarkdownWhitespace(draft.body || "") || "Empty draft";
      const open = draft.commentId === props.openDraftId;
      const canSend = stripMarkdownWhitespace(draft.body || "").length > 0;
      return <HStack key={draft.commentId} borderRadius="md" gap={0} paddingY={0.5}><Box _hover={{
          bg: "colorPalette.subtle"
        }} aria-current={open ? "true" : undefined} aria-label={`Edit draft ${preview}`} as="button" bg={open ? "colorPalette.subtle" : "transparent"} borderRadius="md" flex={1} minWidth={0} paddingX={2} paddingY={1} textAlign="left" type="button" onClick={() => props.onOpen(draft)}><Text fontSize="sm" fontWeight={open ? "medium" : "normal"} lineClamp={1}>{preview}</Text><Text color="colorPalette.fg/50" fontSize="xs">{draftAge(draft.updatedAt || draft.createdAt)}</Text></Box><IconButton aria-label={`Send draft ${preview}`} disabled={!canSend || props.busy} size="xs" variant="ghost" onClick={() => props.onSend(draft)}><LuSend size={14} /></IconButton><IconButton aria-label={`Delete draft ${preview}`} disabled={props.busy} size="xs" variant="ghost" onClick={() => props.onDelete(draft.commentId as string)}><LuTrash2 size={14} /></IconButton></HStack>;
    });
    $[34] = props;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== t10 || $[37] !== t11 || $[38] !== t12) {
    t13 = <HoverCardContent padding={1} width="16rem">{t9}<Box maxHeight="14rem" overflowY="auto">{t10}{t11}{t12}</Box></HoverCardContent>;
    $[36] = t10;
    $[37] = t11;
    $[38] = t12;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== t13 || $[41] !== t8) {
    t14 = <HoverCardRoot closeDelay={200} openDelay={150} positioning={t7}>{t8}{t13}</HoverCardRoot>;
    $[40] = t13;
    $[41] = t8;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  return t14;
}

/** The CRM reply draft's row in the drafts menu: open it, send it through the CRM, or delete it. */
function CrmDraftRow(props) {
  const $ = _c(31);
  let t0;
  if ($[0] !== props.draft.body) {
    t0 = stripMarkdownWhitespace(props.draft.body || "") || "Empty draft";
    $[0] = props.draft.body;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const preview = t0;
  const t1 = props.draft.body || "";
  let t2;
  if ($[2] !== t1) {
    t2 = stripMarkdownWhitespace(t1);
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const canSend = t2.length > 0;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      bg: "colorPalette.subtle"
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = props.open ? "true" : undefined;
  const t5 = `Edit CRM reply draft ${preview}`;
  const t6 = props.open ? "colorPalette.subtle" : "transparent";
  const t7 = props.open ? "medium" : "normal";
  let t8;
  if ($[5] !== preview || $[6] !== t7) {
    t8 = <Text fontSize="sm" fontWeight={t7} lineClamp={1}>{preview}</Text>;
    $[5] = preview;
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const t9 = props.locked ? "CRM reply \xB7 someone is working on it" : "CRM reply \xB7 same draft in your CRM inbox";
  let t10;
  if ($[8] !== t9) {
    t10 = <Text color="colorPalette.fg/50" fontSize="xs">{t9}</Text>;
    $[8] = t9;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== props.onOpen || $[11] !== t10 || $[12] !== t4 || $[13] !== t5 || $[14] !== t6 || $[15] !== t8) {
    t11 = <chakra.button _hover={t3} aria-current={t4} aria-label={t5} bg={t6} borderRadius="md" flex={1} minWidth={0} paddingX={2} paddingY={1} textAlign="left" type="button" onClick={props.onOpen}>{t8}{t10}</chakra.button>;
    $[10] = props.onOpen;
    $[11] = t10;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t8;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const t12 = `Send CRM reply draft ${preview}`;
  const t13 = !canSend || props.busy || props.locked;
  let t14;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <LuSend size={14} />;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] !== props.onSend || $[19] !== t12 || $[20] !== t13) {
    t15 = <IconButton aria-label={t12} disabled={t13} size="xs" variant="ghost" onClick={props.onSend}>{t14}</IconButton>;
    $[18] = props.onSend;
    $[19] = t12;
    $[20] = t13;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  const t16 = `Delete CRM reply draft ${preview}`;
  const t17 = props.busy || props.locked;
  let t18;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <LuTrash2 size={14} />;
    $[22] = t18;
  } else {
    t18 = $[22];
  }
  let t19;
  if ($[23] !== props.onDelete || $[24] !== t16 || $[25] !== t17) {
    t19 = <IconButton aria-label={t16} disabled={t17} size="xs" variant="ghost" onClick={props.onDelete}>{t18}</IconButton>;
    $[23] = props.onDelete;
    $[24] = t16;
    $[25] = t17;
    $[26] = t19;
  } else {
    t19 = $[26];
  }
  let t20;
  if ($[27] !== t11 || $[28] !== t15 || $[29] !== t19) {
    t20 = <HStack borderRadius="md" gap={0} paddingY={0.5}>{t11}{t15}{t19}</HStack>;
    $[27] = t11;
    $[28] = t15;
    $[29] = t19;
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  return t20;
}
function draftAge(value?: string | null) {
  if (!value) {
    return '';
  }
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) {
    return '';
  }
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (minutes < 1) {
    return 'Just now';
  }
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}